import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  PatientAddressDtoReferenceType,
  TherapyDto,
  TherapyDtoTherapyResult,
} from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useEndTherapyByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FileInput } from '../../../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import { formatDateForInput, getCurrentDate, getDateAfterInputValue } from '../../../../../utils/date.ts';
import { blobToBase64 } from '../../../../../utils/file.ts';
import { getTherapyResultName } from '../../../../../utils/therapy.ts';

interface FormValues {
  date: string;
  result: TherapyDtoTherapyResult | null;
  note: string;
  referralAddress: AddressDto | null;
  referralDoctor: AddressContactDto | null;
  supplementDate: string;
  supplementFile: File | null;
}

export function EndTherapyDialog({
  open,
  onClose,
  patientId,
  currentTherapy,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
  currentTherapy: TherapyDto;
}) {
  const queryClient = useQueryClient();
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      date: getCurrentDate(),
      result: null,
      note: '',
      referralAddress: null,
      referralDoctor: null,
      supplementDate: '',
      supplementFile: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const therapyResultValue = watch('result');
  const supplementDateValue = watch('supplementDate');
  const supplementFileValue = watch('supplementFile');
  const therapyEndMinDate =
    getDateAfterInputValue(currentTherapy.startPhase2 ? formatDateForInput(currentTherapy.startPhase2) : undefined) ??
    getDateAfterInputValue(currentTherapy.startPhase1 ? formatDateForInput(currentTherapy.startPhase1) : undefined);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const endTherapyMutation = useEndTherapyByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTherapyByPatientQueryKey(patientId) });
        onClose();
      },
      onError: () => {
        toast.error('Beim Aktualisieren der Therapie ist ein Fehler aufgetreten');
      },
    },
  });

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 900 }}>
        <ModalClose />
        <Typography level={'h4'}>Therapie beenden</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit(async (data) => {
            let referral = undefined;
            if (data.referralAddress) {
              referral = {
                address: data.referralAddress,
                contact: data.referralDoctor ?? undefined,
                from: getCurrentDate(),
                patientId: patientId,
                referenceType: PatientAddressDtoReferenceType.THERAPY_END_REFERRAL,
              };
            }

            endTherapyMutation.mutate({
              id: patientId,
              data: {
                end: data.date,
                result: data.result!,
                note: data.note,
                referral: referral,
                supplementDate: data.supplementDate || null,
                supplementFile: data.supplementFile
                  ? {
                      fileName: data.supplementFile.name,
                      file: await blobToBase64(data.supplementFile),
                    }
                  : null,
              },
            });
          })}>
          <Stack gap={2}>
            <FormControl error={!!errors.date}>
              <FormLabel>
                <Typography>End-Datum *</Typography>
              </FormLabel>
              <GermanDateInput
                control={control}
                name="date"
                rules={{
                  required: true,
                  ...(therapyEndMinDate ? { min: { value: therapyEndMinDate, message: 'range' } } : {}),
                }}
                min={therapyEndMinDate}
              />
              {errors.date && (
                <FormHelperText>
                  {errors.date.type === 'required'
                    ? 'Datum muss ausgefüllt werden'
                    : 'End-Datum muss nach dem Therapiebeginn liegen'}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl error={!!errors.result}>
              <FormLabel>
                <Typography>Resultat *</Typography>
              </FormLabel>
              <Controller
                name={'result'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={(_, value) => onChange(value)}>
                    {Object.values(TherapyDtoTherapyResult)
                      .filter((type) => type !== TherapyDtoTherapyResult.PENDING)
                      .map((type) => (
                        <Option value={type} key={type}>
                          {getTherapyResultName(type)}
                        </Option>
                      ))}
                  </Select>
                )}
              />
              {errors.result && <FormHelperText>Resultat der Therapie muss angegeben werden</FormHelperText>}
            </FormControl>

            {therapyResultValue === TherapyDtoTherapyResult.REFERRAL && (
              <InstitutionContactFields
                formMethods={{ control, watch, setValue }}
                addressField="referralAddress"
                contactField="referralDoctor"
                addressLabel="Überweisungs-Adresse *"
                contactLabel="Überweisungs-Kontakt"
                addressTypes={Object.values(AddressDtoAddressType)}
                canChangeAddressType={false}
                addressRules={{ required: therapyResultValue === TherapyDtoTherapyResult.REFERRAL }}
                addressError={!!errors.referralAddress}
                addressHelperText={errors.referralAddress ? 'Adresse für Überweisung muss angegeben werden' : undefined}
                clearContactOnAddressChange="always"
                contactClearOptions={false}
                useAllContactsList={true}
              />
            )}

            <Stack direction={'row'} gap={2}>
              <FormControl sx={{ flex: 1 }} error={!!errors.supplementFile}>
                <FormLabel>
                  <Typography>Ergänzungsmeldung Datei {supplementDateValue ? '*' : ''}</Typography>
                </FormLabel>
                <Controller
                  name={'supplementFile'}
                  control={control}
                  rules={{ required: !!supplementDateValue }}
                  render={({ field: { value, onChange } }) => (
                    <FileInput
                      value={value}
                      onChange={onChange}
                      accept={'application/pdf'}
                      placeholder={'Datei hochladen'}
                    />
                  )}
                />
                {errors.supplementFile && <FormHelperText>Datei muss hochgeladen werden</FormHelperText>}
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={!!errors.supplementDate}>
                <FormLabel>
                  <Typography>Ergänzungsmeldung Datum {supplementFileValue ? '*' : ''}</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="supplementDate"
                  rules={{
                    required: !!supplementFileValue,
                  }}
                />
                {errors.supplementDate && <FormHelperText>Datum muss angegeben werden</FormHelperText>}
              </FormControl>
            </Stack>

            <FormControl error={!!errors.note}>
              <FormLabel>
                <Typography>Bemerkung</Typography>
              </FormLabel>
              <Textarea {...register('note')} minRows={4} maxRows={4} />
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={endTherapyMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
